import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Tooltip, CircleMarker, Marker, Circle, Rectangle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Icône "épingle GPS" en divIcon (pas de dépendance aux images marker-icon.png
// par défaut de Leaflet, qui posent souvent problème avec les bundlers).
const gpsIcon = L.divIcon({
  className: 'no-print',
  html: '<div style="font-size:30px;line-height:30px;filter:drop-shadow(0 2px 2px rgba(0,0,0,0.35));transform:translate(-50%,-90%);">📍</div>',
  iconSize: [30, 30],
  iconAnchor: [0, 0],
});

const API = '/api/cadastre';
function getToken() { return localStorage.getItem('authToken') || ''; }
function headers() { return { Authorization: `Bearer ${getToken()}` }; }

// Codes techno Arcep ("Ma connexion internet") — nomenclature officielle stable.
const TECHNO_LABELS: Record<string, string> = {
  FO: 'Fibre optique (FTTH)',
  CU: 'Cuivre (ADSL/VDSL)',
  CA: 'Câble',
  '4GF': '4G fixe',
  SAT: 'Satellite',
  THD_RADIO: 'THD radio',
};

// Classes de débit réellement utilisées dans les fichiers Arcep "Ma connexion
// internet" (colonne classe_debit_descendant/montant). Ce ne sont PAS des
// numéros 1-7 mais des codes texte (bon débit / très haut débit).
const DEBIT_CLASSE_LABELS: Record<string, string> = {
  INEL: 'Inéligible',
  RIP: 'Inéligible',
  BD: '3 à 8 Mb/s (bon débit)',
  BD8: '8 à 30 Mb/s (bon débit)',
  THD30: '30 à 100 Mb/s',
  THD100: '100 Mb/s à 1 Gb/s',
  THD1G: '1 Gb/s et plus',
  // anciens millésimes / variantes numériques, gardés en secours
  '0': 'Inéligible',
  '1': '512 kb/s à 4 Mb/s',
  '2': '2 à 10 Mb/s',
  '3': '6 à 25 Mb/s',
  '4': '20 à 100 Mb/s',
  '5': '100 Mb/s à 1 Gb/s',
  '6': '100 Mb/s à 1 Gb/s',
  '7': '1 Gb/s et plus',
};

function formatDebit(classe: string | null): string | null {
  if (classe == null || classe === '') return null;
  if (DEBIT_CLASSE_LABELS[classe]) return DEBIT_CLASSE_LABELS[classe];
  // Fallback générique pour un code THD<nombre>[G] non listé explicitement.
  const m = /^THD(\d+)(G)?$/i.exec(classe);
  if (m) {
    const val = m[1];
    const unit = m[2] ? 'Gb/s' : 'Mb/s';
    return `${val} ${unit} et plus`;
  }
  return classe;
}

interface EtablissementProche {
  nom: string;
  commune: string | null;
  distanceVolOiseauKm: number | null;
  distanceRouteKm: number | null;
  dureeVoitureMin: number | null;
}

const NIVEAU_LABELS: Record<string, string> = {
  maternelle: 'École maternelle',
  primaire: 'École primaire',
  college: 'Collège',
  lycee: 'Lycée',
};

interface TrajetVoiture {
  distanceVolOiseauKm: number | null;
  distanceRouteKm: number | null;
  dureeVoitureMin: number | null;
}

function formatTrajet(e?: TrajetVoiture | null): string {
  if (!e) return '';
  if (e.dureeVoitureMin != null) {
    const km = e.distanceRouteKm != null ? ` (${e.distanceRouteKm} km)` : '';
    return `${e.dureeVoitureMin} min en voiture${km}`;
  }
  if (e.distanceVolOiseauKm != null) return `${e.distanceVolOiseauKm} km à vol d'oiseau`;
  return '';
}

interface FicheCadastrale {
  adresse: {
    label: string;
    ville: string;
    codePostal: string;
    codeInsee: string;
    positionEstimated?: boolean;
    lon: number;
    lat: number;
  };
  parcelle: {
    idu: string;
    section: string;
    numero: string;
    feuille: number | null;
    codeDep: string;
    codeCom: string;
    comAbs: string;
    codeArr: string | null;
    codeInsee: string;
    nomCom: string;
    contenanceM2: number;
    geometry: { type: string; coordinates: any };
  };
  zonage: {
    libelle: string;
    libelong: string;
    typezone: string;
    dateValidite: string | null;
    idUrba: string | null;
    ambigu?: boolean;
    zoneAlternative?: { libelle: string; libelong: string; typezone: string } | null;
  } | null;
  ventes: Array<{
    date: string;
    annee: number;
    valeur: number;
    composantes: Array<{ type: string | null; surface: number | null }>;
    nbTerrains?: number;
    surfaceTerrain?: number | null;
    parcelles?: string[];
    surfaceHabitation: number | null;
    prixM2: number | null;
  }>;
  batiments: Array<{
    id: string;
    usage: string | null;
    hauteurM: number | null;
    nombreEtages: number | null;
    nombreLogements: number | null;
    materiauxMurs: string | null;
    materiauxToiture: string | null;
    empriseSolM2: number | null;
    anneeConstruction: number | null;
    anneeConstructionDpe?: number | null;
    periodeConstructionDpe?: string | null;
    ancienneteBati?: AncienneteBati | null;
  }>;
  eligibiliteInternet: {
    status: 'ready' | 'importing' | 'unavailable';
    technologies?: Array<{
      technologie: string | null;
      debitDescendant: string | null;
      debitMontant: string | null;
      operateur: string | null;
    }> | null;
    distanceM?: number | null;
  } | null;
  gareSncf: {
    status: 'ready' | 'importing' | 'unavailable';
    gare?: {
      nom: string;
      commune: string | null;
      distanceVolOiseauKm: number | null;
      distanceRouteKm: number | null;
      dureeVoitureMin: number | null;
    } | null;
  } | null;
  ecoles: {
    status: 'ready' | 'importing' | 'unavailable';
    maternelle?: EtablissementProche | null;
    primaire?: EtablissementProche | null;
    college?: EtablissementProche | null;
    lycee?: EtablissementProche | null;
  } | null;
  sortieAutoroute: {
    status: 'ready' | 'importing' | 'unavailable';
    sortie?: {
      nom: string;
      numero: string | null;
      distanceVolOiseauKm: number | null;
      distanceRouteKm: number | null;
      dureeVoitureMin: number | null;
    } | null;
  } | null;
  risquesNaturels: {
    sismicite: { zone: string; libelle: string } | null;
    argiles: { code: string; libelle: string } | null;
    radon: { classe: string } | null;
    nbCavites: number;
    sitesPollues: Array<{ nom: string; adresse: string | null; statut: string | null; distanceM: number | null }>;
    installationsClassees: Array<{ nom: string; commune: string | null; seveso: string | null; distanceM: number | null }>;
    catnat: Array<{ libelle: string; dateDebut: string | null; dateFin: string | null; datePublication: string | null }>;
  } | null;
  dpe: {
    numeroDpe: string;
    adresse: string | null;
    adresseBrute: string | null;
    adresseBan: string | null;
    nomResidence: string | null;
    complementAdresseBatiment: string | null;
    complementAdresseLogement: string | null;
    typeBatiment: string | null;
    periodeConstruction: string | null;
    surfaceHabitable: number | null;
    etiquetteDpe: string | null;
    etiquetteGes: string | null;
    dateEtablissement: string | null;
    consoEnergiePrimaire: number | null;
    emissionGes: number | null;
    typeEnergiePrincipaleChauffage: string | null;
    typeEnergiePrincipaleEcs: string | null;
    coutTotal5Usages: number | null;
    coutChauffage: number | null;
    lienAdeme: string;
  } | null;
}

const DPE_COLORS: Record<string, { bg: string; text: string }> = {
  A: { bg: '#009B60', text: '#fff' },
  B: { bg: '#3DAE4E', text: '#fff' },
  C: { bg: '#C8D92A', text: '#1f2937' },
  D: { bg: '#FEDA00', text: '#1f2937' },
  E: { bg: '#F7A600', text: '#1f2937' },
  F: { bg: '#E9563D', text: '#fff' },
  G: { bg: '#CE1727', text: '#fff' },
};

function DpeBadge({ letter, label }: { letter: string | null; label: string }) {
  if (!letter) return null;
  const colors = DPE_COLORS[letter.toUpperCase()] || { bg: '#9ca3af', text: '#fff' };
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl shadow-sm"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        {letter.toUpperCase()}
      </div>
      <span className="text-[11px] text-gray-500 font-medium">{label}</span>
    </div>
  );
}

// Ligne d'info DPE façon liste propre : icône dans un rond, libellé à gauche,
// valeur alignée à droite — ne s'affiche pas si la donnée est absente.
function DpeInfoRow({ icon, label, value, note }: { icon: string; label: string; value: string | null; note?: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-sm">
      <span className="flex items-center gap-2 text-gray-500 shrink-0">
        <span className="w-6 h-6 rounded-full bg-white border border-teal-100 flex items-center justify-center text-xs shrink-0">{icon}</span>
        {label}
      </span>
      <span className="font-semibold text-gray-900 text-right">
        {value}
        {note && <span className="block text-[11px] font-normal text-gray-400 mt-0.5">{note}</span>}
      </span>
    </div>
  );
}

const NIVEAU_ICONS: Record<string, string> = {
  maternelle: '🧸',
  primaire: '📖',
  college: '🎒',
  lycee: '🎓',
};

// Ligne "établissement le plus proche" — même esprit visuel que DpeInfoRow
// (icône + libellé à gauche, nom en gras à droite, trajet en note dessous).
function EcoleInfoRow({ niveau, e }: { niveau: 'maternelle' | 'primaire' | 'college' | 'lycee'; e?: EtablissementProche | null }) {
  const trajet = formatTrajet(e);
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-sm">
      <span className="flex items-center gap-2 text-gray-500 shrink-0">
        <span className="w-6 h-6 rounded-full bg-white border border-violet-100 flex items-center justify-center text-xs shrink-0">
          {NIVEAU_ICONS[niveau]}
        </span>
        {NIVEAU_LABELS[niveau]}
      </span>
      <span className="font-semibold text-gray-900 text-right">
        {e ? e.nom : <span className="font-normal text-gray-400">aucune référencée à proximité</span>}
        {trajet && <span className="block text-[11px] font-normal text-violet-500 mt-0.5">{trajet}</span>}
      </span>
    </div>
  );
}

// Tuile info DPE façon "carte" (même esprit visuel que les tuiles Énergie/Coûts) —
// icône + libellé au-dessus, valeur en gras en dessous.
function DpeInfoTile({ icon, label, value }: { icon: string; label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-teal-600 mb-0.5">{icon} {label}</p>
      <p className="font-semibold text-gray-900">{value ?? '—'}</p>
    </div>
  );
}

// Ligne d'info générique (référence, section, commune, surface, GPS...) —
// icône colorée à gauche, libellé, valeur alignée à droite. Le texte peut être
// long (ex: référence cadastrale) sans jamais déborder du cadre.
function InfoRow({ icon, iconBg, label, value }: { icon: string; iconBg: string; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 text-sm">
      <span className="flex items-center gap-2.5 text-gray-500 min-w-0 shrink-0">
        <span className={`info-ico w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0 ${iconBg}`}>{icon}</span>
        {label}
      </span>
      <span className="font-semibold text-gray-900 text-right break-words">{value}</span>
    </div>
  );
}

// Pastille de niveau de risque (sismicité, argiles, radon...) — couleur selon
// la position du niveau sur SON échelle réelle (toutes ne font pas la même
// longueur : sismicité va de 1 à 5, argiles/radon de 1 à 3 — un "3" ne veut
// donc pas dire la même chose dans les deux cas).
function NiveauPill({ label, niveau, libelle, echelleMax = 3 }: { label: string; niveau: string; libelle: string; echelleMax?: number }) {
  const n = parseInt(niveau, 10) || 0;
  const ratio = echelleMax > 1 ? (n - 1) / (echelleMax - 1) : 0; // 0 = niveau le plus faible, 1 = le plus fort
  const cls = ratio <= 0.34 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : ratio <= 0.67 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`inline-flex flex-col text-xs px-2.5 py-1.5 rounded-lg border ${cls}`}>
      <span className="font-semibold">{label}</span>
      <span className="opacity-80">{libelle}</span>
    </span>
  );
}

const TYPEZONE_LABELS: Record<string, string> = {
  U:  'Zone urbaine',
  AU: 'Zone à urbaniser',
  A:  'Zone agricole',
  N:  'Zone naturelle',
};

function formatPrice(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

interface ParcelleZone {
  idu: string;
  section: string;
  numero: string;
  codeDep: string;
  codeCom: string;
  nomCom: string;
  codeInsee: string;
  contenanceM2: number;
  geometry: { type: string; coordinates: any };
  centroid: [number, number];
  aVenteDvf: boolean;
}

interface ZoneResult {
  parcelles: ParcelleZone[];
  total: number;
  capped: boolean;
  surfaceTotaleM2: number;
  nbAvecVenteDvf: number;
}

type ZoneShape = 'circle' | 'donut' | 'rectangle';

interface Suggestion {
  label: string;
  city: string;
  postcode: string;
  citycode: string;
  type: string;
  source?: string;
  positionEstimated?: boolean;
  lon: number;
  lat: number;
}

// Infos de l'agent connecté, pour personnaliser l'en-tête du document
// imprimé (nom, activité, contact, photo) — voir /api/cadastre/agent-profile.
interface AncienneteBati {
  valeur: string;
  niveau: 'concordante' | 'source_unique' | 'a_verifier';
  sources: string[];
  explication: string;
}

interface AgentProfile {
  name: string;
  activite: string | null;
  entreprise: string | null;
  ville: string | null;
  telephone: string | null;
  siteWeb: string | null;
  photoUrl: string | null;
  couleurMarque?: string | null;
}

// Année de construction présentée honnêtement : valeur retenue, niveau de
// certitude, sources croisées et moyen de vérification. Aucune base publique
// ne date un bâtiment avec certitude — le conseiller doit savoir quand la
// donnée est solide (sources concordantes) et quand elle est à confirmer.
const NIVEAUX_ANCIENNETE: Record<AncienneteBati['niveau'], { label: string; cls: string }> = {
  concordante:   { label: 'Sources concordantes', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  source_unique: { label: 'Source unique',        cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  a_verifier:    { label: 'À vérifier',           cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function AncienneteCarte({ anciennete, lon, lat }: { anciennete: AncienneteBati | null; lon: number; lat: number }) {
  const lienIgn = `https://remonterletemps.ign.fr/comparer/?lon=${lon}&lat=${lat}&z=18&layer1=10&layer2=19&mode=split-h`;
  const niveau = anciennete ? NIVEAUX_ANCIENNETE[anciennete.niveau] : null;
  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3 mb-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs text-gray-500">Année de construction</p>
          <p className="text-base font-bold text-gray-900">{anciennete?.valeur || 'Non renseignée'}</p>
        </div>
        {niveau && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border shrink-0 ${niveau.cls}`}>{niveau.label}</span>
        )}
      </div>
      {anciennete && anciennete.niveau !== 'concordante' && (
        <p className="text-xs text-gray-600 mt-1.5">{anciennete.explication}</p>
      )}
      {anciennete && anciennete.sources.length > 0 && (
        <p className="text-xs text-gray-400 mt-1">{anciennete.sources.join(' · ')}</p>
      )}
      {(!anciennete || anciennete.niveau === 'a_verifier') && (
        <>
          <div className="no-print mt-2 flex items-center gap-2 flex-wrap">
            <a
              href={lienIgn}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              📷 Comparer avec les photos aériennes 1950-1965 (IGN) ↗
            </a>
          </div>
          <p className="no-print text-[11px] text-gray-400 mt-1.5">
            Bâtiment visible en 1950-1965 = construit avant. Pour une date certaine : acte de propriété
            (un CREP plomb dans les diagnostics annexés = construction avant 1949) ou archives des permis en mairie.
          </p>
          <p className="print-only text-xs text-gray-500 mt-1.5">
            Date à confirmer par l'acte de propriété ou les photographies aériennes historiques de l'IGN.
          </p>
        </>
      )}
    </div>
  );
}

function formatDateFr(d: string) {
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('fr-FR');
}

function formatSurfaceCadastrale(m2: number) {
  const total = Math.round(m2);
  const ha = Math.floor(total / 10000);
  const rest1 = total % 10000;
  const a = Math.floor(rest1 / 100);
  const ca = rest1 % 100;
  const parts: string[] = [];
  if (ha) parts.push(`${ha} ha`);
  if (a || ha) parts.push(`${a} a`);
  parts.push(`${ca} ca`);
  return parts.join(' ');
}

// Projection équirectangulaire simple (correcte à l'échelle d'une parcelle) :
// x = lon * cos(lat0), y = lat, puis mise à l'échelle dans un viewBox carré.
function ParcellePlan({ geometry, contenanceM2 }: { geometry: FicheCadastrale['parcelle']['geometry']; contenanceM2: number }) {
  const { pathD, scaleBarLabel, scaleBarPx } = useMemo(() => {
    const polygons: [number, number][][][] =
      geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];

    const allPoints: [number, number][] = [];
    polygons.forEach(poly => poly.forEach(ring => ring.forEach(pt => allPoints.push(pt))));

    const lat0 = allPoints.reduce((s, p) => s + p[1], 0) / allPoints.length;
    const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
    const metersPerDegLon = 111320 * cosLat0;
    const metersPerDegLat = 111320;

    // Conversion lon/lat -> mètres locaux (repère plan tangent)
    const toMeters = (lon: number, lat: number): [number, number] => [
      lon * metersPerDegLon,
      lat * metersPerDegLat,
    ];

    const metersPoints = allPoints.map(([lon, lat]) => toMeters(lon, lat));
    const xs = metersPoints.map(p => p[0]);
    const ys = metersPoints.map(p => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const width = maxX - minX || 1;
    const height = maxY - minY || 1;
    const size = Math.max(width, height) * 1.25; // marge 25%
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const VIEWBOX = 400;
    const scale = VIEWBOX / size;
    // x -> droite, y (lat, nord) -> vers le haut donc on inverse pour SVG (y croissant vers le bas)
    const project = (mx: number, my: number): [number, number] => [
      VIEWBOX / 2 + (mx - cx) * scale,
      VIEWBOX / 2 - (my - cy) * scale,
    ];

    const ringToPath = (ring: [number, number][]) => {
      const pts = ring.map(([lon, lat]) => project(...toMeters(lon, lat)));
      return 'M ' + pts.map(p => p.join(',')).join(' L ') + ' Z';
    };

    const pathD = polygons
      .map(poly => poly.map(ring => ringToPath(ring)).join(' '))
      .join(' ');

    // Barre d'échelle : on choisit un nombre "rond" de mètres proche de 1/4 de la taille affichée
    const targetMeters = size / 4;
    const niceSteps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
    const niceMeters = niceSteps.reduce((best, step) =>
      Math.abs(step - targetMeters) < Math.abs(best - targetMeters) ? step : best
    , niceSteps[0]);
    const scaleBarPx = niceMeters * scale;

    return { pathD, scaleBarLabel: `${niceMeters} m`, scaleBarPx };
  }, [geometry]);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 400 400" className="w-full max-w-sm border border-gray-200 rounded-xl bg-blue-50/40">
        {/* Flèche du nord */}
        <g transform="translate(368, 32)">
          <line x1="0" y1="18" x2="0" y2="-10" stroke="#6b7280" strokeWidth="1.5" />
          <polygon points="0,-14 -5,-4 5,-4" fill="#6b7280" />
          <text x="0" y="30" textAnchor="middle" fontSize="11" fill="#6b7280">N</text>
        </g>
        <path d={pathD} fill="#c7d2fe" stroke="#4338ca" strokeWidth="2" fillRule="evenodd" />
        {/* Barre d'échelle */}
        <g transform={`translate(20, 378)`}>
          <line x1="0" y1="0" x2={scaleBarPx} y2="0" stroke="#374151" strokeWidth="2" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke="#374151" strokeWidth="2" />
          <line x1={scaleBarPx} y1="-4" x2={scaleBarPx} y2="4" stroke="#374151" strokeWidth="2" />
          <text x={scaleBarPx / 2} y="-8" textAnchor="middle" fontSize="11" fill="#374151">{scaleBarLabel}</text>
        </g>
      </svg>
      <p className="text-xs text-gray-400 mt-2">
        Plan schématique généré à partir du contour cadastral officiel — {contenanceM2.toLocaleString('fr-FR')} m² au sol
      </p>
    </div>
  );
}

// Taille (px) de la vue satellite ci-dessous — carrée, image + calque SVG alignés au pixel près.
const SAT_SIZE = 640;

// Vue satellite de la parcelle (orthophoto IGN), avec le contour cadastral et
// l'épingle de l'adresse recherchée superposés — c'est cette vue (photo
// aérienne réelle) qui est utilisée à l'impression, pas seulement le schéma
// vectoriel. Une vraie balise <img>/<svg> (pas un fond CSS) : elle s'imprime
// donc toujours, même navigateur "graphiques d'arrière-plan" désactivé.
// Si l'image satellite ne charge pas (réseau, service indisponible), on
// retombe automatiquement sur le plan schématique ci-dessus.
function ParcelleSatellite({ geometry, contenanceM2, lon, lat }: { geometry: FicheCadastrale['parcelle']['geometry']; contenanceM2: number; lon: number; lat: number }) {
  const [imgError, setImgError] = useState(false);

  const { bbox, pathD, markerXY, scaleBarLabel, scaleBarPx } = useMemo(() => {
    const polygons: [number, number][][][] =
      geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
    const allPoints: [number, number][] = [];
    polygons.forEach(poly => poly.forEach(ring => ring.forEach(pt => allPoints.push(pt))));

    const lat0 = allPoints.reduce((s, p) => s + p[1], 0) / allPoints.length;
    const lon0 = allPoints.reduce((s, p) => s + p[0], 0) / allPoints.length;
    const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
    const metersPerDegLon = 111320 * cosLat0;
    const metersPerDegLat = 111320;

    const toMeters = (plon: number, plat: number): [number, number] => [
      (plon - lon0) * metersPerDegLon,
      (plat - lat0) * metersPerDegLat,
    ];

    const metersPoints = allPoints.map(([plon, plat]) => toMeters(plon, plat));
    const xs = metersPoints.map(p => p[0]);
    const ys = metersPoints.map(p => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const width = maxX - minX || 1;
    const height = maxY - minY || 1;
    // Marge large (x1.4) pour voir le contexte alentour (accès, voisinage) — pas juste la parcelle nue.
    const half = Math.max(width, height) * 1.4;

    const minLon = lon0 - half / metersPerDegLon;
    const maxLon = lon0 + half / metersPerDegLon;
    const minLat = lat0 - half / metersPerDegLat;
    const maxLat = lat0 + half / metersPerDegLat;

    const project = (plon: number, plat: number): [number, number] => [
      ((plon - minLon) / (maxLon - minLon)) * SAT_SIZE,
      ((maxLat - plat) / (maxLat - minLat)) * SAT_SIZE,
    ];

    const ringToPath = (ring: [number, number][]) => {
      const pts = ring.map(([plon, plat]) => project(plon, plat));
      return 'M ' + pts.map(p => p.join(',')).join(' L ') + ' Z';
    };
    const pathD = polygons.map(poly => poly.map(ring => ringToPath(ring)).join(' ')).join(' ');

    const markerXY = project(lon, lat);

    const targetMeters = (half * 2) / 4;
    const niceSteps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
    const niceMeters = niceSteps.reduce((best, step) =>
      Math.abs(step - targetMeters) < Math.abs(best - targetMeters) ? step : best
    , niceSteps[0]);
    const scaleBarPx = (niceMeters / (half * 2)) * SAT_SIZE;

    return {
      // WMS 1.3.0 + CRS=EPSG:4326 exige l'ordre latitude,longitude (contrairement à 1.1.1) —
      // erreur "VersionNegotiationFailed" confirmée en testant le service en direct.
      bbox: `${minLat},${minLon},${maxLat},${maxLon}`,
      pathD, markerXY, scaleBarLabel: `${niceMeters} m`, scaleBarPx,
    };
  }, [geometry, lon, lat]);

  if (imgError) {
    return <ParcellePlan geometry={geometry} contenanceM2={contenanceM2} />;
  }

  // Passe par notre propre serveur (voir server/src/routes/cadastre.js, route
  // /satellite) plutôt que d'appeler directement data.geopf.fr : une image
  // chargée en direct depuis l'IGN est cross-origin et "tache" le canvas lors
  // de la génération du PDF (html2canvas/export impossible), car le service
  // IGN n'envoie pas d'en-têtes CORS permissifs. En même origine, plus de souci.
  const imgSrc = `${API}/satellite?bbox=${encodeURIComponent(bbox)}&size=${SAT_SIZE}`;

  return (
    <div className="flex flex-col items-center">
      <div className="relative rounded-xl overflow-hidden border border-gray-200 w-full max-w-md" style={{ aspectRatio: '1 / 1' }}>
        <img
          src={imgSrc}
          alt="Vue satellite de la parcelle"
          crossOrigin="anonymous"
          onError={() => setImgError(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <svg viewBox={`0 0 ${SAT_SIZE} ${SAT_SIZE}`} className="absolute inset-0 w-full h-full">
          <path d={pathD} fill="rgba(139,92,246,0.28)" stroke="#8b5cf6" strokeWidth="4" fillRule="evenodd" />
          <circle cx={markerXY[0]} cy={markerXY[1]} r="8" fill="#ef4444" stroke="#fff" strokeWidth="2.5" />
          <g transform={`translate(16, ${SAT_SIZE - 20})`}>
            <line x1="0" y1="0" x2={scaleBarPx} y2="0" stroke="#fff" strokeWidth="5" />
            <line x1="0" y1="0" x2={scaleBarPx} y2="0" stroke="#111827" strokeWidth="2" />
            <text x={scaleBarPx / 2} y="-6" textAnchor="middle" fontSize="13" fill="#111827" stroke="#fff" strokeWidth="3" paintOrder="stroke" fontWeight="600">{scaleBarLabel}</text>
          </g>
        </svg>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Vue satellite (IGN — orthophotographie) — parcelle en violet, adresse recherchée en rouge — {contenanceM2.toLocaleString('fr-FR')} m² au sol
      </p>
    </div>
  );
}

// Seuil au-delà duquel on arrête de dessiner le contour complet de chaque
// parcelle (des centaines de polygones détaillés font ramer le navigateur) et
// on bascule sur de simples points — la liste/tableau reste complète dans
// tous les cas, seul le rendu carte est allégé.
const ZONE_MAP_POLYGON_LIMIT = 250;

// Recentre/déplace la carte quand le point de référence change (nouvelle
// adresse recherchée) — MapContainer ne le fait pas tout seul après le
// montage initial.
function RecenterMap({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], map.getZoom());
  }, [lat, lon]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Même conversion mètres → degrés que côté serveur (CadastreService.js) —
// utilisée uniquement pour dessiner le contour de la zone de recherche et
// cadrer la carte dessus, jamais pour la requête elle-même (faite côté API).
function metresToDegreesClient(lat: number, radiusM: number) {
  const latRad = (lat * Math.PI) / 180;
  return {
    dLat: radiusM / 111320,
    dLon: radiusM / (111320 * Math.max(Math.cos(latRad), 0.2)),
  };
}

interface ZoneParams {
  shape: ZoneShape;
  radiusM: number;
  innerRadiusM: number;
  widthM: number;
  heightM: number;
}

// Cadre automatiquement la carte sur toute l'étendue de la zone de recherche
// (cercle/donut/rectangle) à chaque nouvelle recherche — sans ça, changer de
// forme ou de rayon peut ne rien changer à l'écran si la différence se joue
// hors du cadre actuellement visible.
function FitZone({ lat, lon, zone }: { lat: number; lon: number; zone: ZoneParams | null }) {
  const map = useMap();
  useEffect(() => {
    if (!zone) return;
    const halfLat = zone.shape === 'rectangle' ? zone.heightM / 2 : zone.radiusM;
    const halfLon = zone.shape === 'rectangle' ? zone.widthM / 2 : zone.radiusM;
    const { dLat } = metresToDegreesClient(lat, halfLat);
    const { dLon } = metresToDegreesClient(lat, halfLon);
    map.fitBounds([[lat - dLat, lon - dLon], [lat + dLat, lon + dLon]], { padding: [30, 30] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, zone?.shape, zone?.radiusM, zone?.widthM, zone?.heightM]);
  return null;
}

// Contour visuel (pointillé) de la zone de recherche — cercle, donut ou
// rectangle — pour que le changement de forme/rayon soit visible même quand
// le jeu de parcelles obtenu est proche du précédent.
function ZoneBoundary({ lat, lon, zone }: { lat: number; lon: number; zone: ZoneParams }) {
  const boundaryStyle = { color: '#8b5cf6', weight: 2, dashArray: '8 8', fill: false as const };
  if (zone.shape === 'rectangle') {
    const { dLat } = metresToDegreesClient(lat, zone.heightM / 2);
    const { dLon } = metresToDegreesClient(lat, zone.widthM / 2);
    return <Rectangle bounds={[[lat - dLat, lon - dLon], [lat + dLat, lon + dLon]]} pathOptions={boundaryStyle} />;
  }
  if (zone.shape === 'donut') {
    return (
      <>
        <Circle center={[lat, lon]} radius={zone.radiusM} pathOptions={boundaryStyle} />
        {zone.innerRadiusM > 0 && <Circle center={[lat, lon]} radius={zone.innerRadiusM} pathOptions={boundaryStyle} />}
      </>
    );
  }
  return <Circle center={[lat, lon]} radius={zone.radiusM} pathOptions={boundaryStyle} />;
}

// Convertit une géométrie GeoJSON (lon,lat) en positions Leaflet (lat,lon).
function toLeafletPositions(geometry: { type: string; coordinates: any }): [number, number][][][] {
  const polygons: [number, number][][][] =
    geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
  return polygons.map(poly => poly.map(ring => ring.map(([plon, plat]: [number, number]) => [plat, plon] as [number, number])));
}

// Zoome sur la parcelle sélectionnée (clic carte OU clic tableau) : cadre
// précisément son contour réel, pas juste son centroïde — que ça vienne d'un
// clic sur la carte ou d'une ligne du tableau, la parcelle choisie doit
// remplir l'écran pour qu'on voie tout de suite son détail (bâti, forme...).
function ZoomToSelected({ idu, geometry }: { idu?: string; geometry: { type: string; coordinates: any } | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    const positions = toLeafletPositions(geometry).flat(2);
    if (!positions.length) return;
    const bounds = L.latLngBounds(positions);
    map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 19, duration: 0.6 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idu]);
  return null;
}

// La carte unique de la page : centrée sur l'adresse recherchée (épingle GPS
// dessus), toutes les parcelles alentour dessinées et cliquables.
// Trois états visuels distincts, pour qu'on sache toujours où on en est :
//  - au survol : la parcelle s'éclaircit, son contour s'épaissit, une info-bulle
//    suit le curseur (référence + surface) ;
//  - sélectionnée (celle affichée dans le panneau de droite) : halo blanc épais
//    + remplissage marqué, elle passe au premier plan ;
//  - parcelle de l'adresse recherchée : violet, contour pointillé si une autre
//    parcelle est sélectionnée (elle reste repérable sans voler la vedette).
function CadastreMap({
  lat, lon, parcelles, onSelect, highlightIdu, selectedIdu, zone,
}: {
  lat: number; lon: number; parcelles: ParcelleZone[]; onSelect: (p: ParcelleZone) => void; highlightIdu?: string; selectedIdu?: string; zone: ZoneParams | null;
}) {
  const simplified = parcelles.length > ZONE_MAP_POLYGON_LIMIT;
  const [hoveredIdu, setHoveredIdu] = useState<string | null>(null);

  const selected = useMemo(
    () => parcelles.find(p => p.idu === selectedIdu) || null,
    [parcelles, selectedIdu]
  );

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 relative">
      {/* Curseur "main" sur les parcelles + transitions douces au survol : sans ça
          rien n'indique que les polygones sont cliquables. */}
      <style>{`
        .cadastre-map .leaflet-interactive {
          cursor: pointer;
          transition: fill-opacity .12s ease-out, stroke-width .12s ease-out, stroke-opacity .12s ease-out;
        }
        .cadastre-map .leaflet-tooltip.parcelle-tip {
          background: rgba(17,24,39,.92); color: #fff; border: none; box-shadow: 0 2px 8px rgba(0,0,0,.3);
          font-size: 11px; line-height: 1.35; padding: 5px 8px; border-radius: 8px; white-space: nowrap;
        }
        .cadastre-map .leaflet-tooltip.parcelle-tip::before { display: none; }
        .cadastre-map .leaflet-popup-content-wrapper {
          border-radius: 14px;
          box-shadow: 0 10px 28px rgba(0,0,0,.16);
          padding: 0;
        }
        .cadastre-map .leaflet-popup-content { margin: 0; width: auto !important; }
        .cadastre-map .leaflet-popup-tip { box-shadow: 0 4px 10px rgba(0,0,0,.1); }
        .cadastre-map .leaflet-popup-close-button {
          color: #d1d5db !important;
          font-size: 18px !important;
          padding: 8px 10px 0 0 !important;
          transition: color .12s ease-out;
        }
        .cadastre-map .leaflet-popup-close-button:hover { color: #6b7280 !important; }
      `}</style>
      <MapContainer className="cadastre-map" center={[lat, lon]} zoom={17} style={{ height: 680, width: '100%' }} scrollWheelZoom={true}>
        <RecenterMap lat={lat} lon={lon} />
        <FitZone lat={lat} lon={lon} zone={zone} />
        <ZoomToSelected idu={selected?.idu} geometry={selected ? selected.geometry : null} />
        <TileLayer
          attribution='&copy; IGN-F/Géoportail'
          url="https://data.geopf.fr/wmts?LAYER=ORTHOIMAGERY.ORTHOPHOTOS&EXCEPTIONS=text/xml&FORMAT=image/jpeg&SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
          maxZoom={20}
        />
        {zone && <ZoneBoundary lat={lat} lon={lon} zone={zone} />}
        {parcelles.map(p => {
          const isHighlight = !!highlightIdu && p.idu === highlightIdu;
          const isSelected = !!selectedIdu && p.idu === selectedIdu;
          const isHovered = hoveredIdu === p.idu;
          const color = isHighlight ? '#8b5cf6' : p.aVenteDvf ? '#fb923c' : '#4ade80';

          const tip = (
            <Tooltip sticky direction="top" offset={[0, -6]} opacity={1} className="parcelle-tip">
              <span style={{ fontWeight: 700 }}>{p.section} {p.numero}</span>
              {' · '}{p.contenanceM2.toLocaleString('fr-FR')} m²
              {p.aVenteDvf && <span style={{ color: '#fdba74' }}> · vente DVF</span>}
              <br />
              <span style={{ opacity: .7 }}>{isSelected ? 'Sélectionnée' : 'Cliquer pour voir la fiche'}</span>
            </Tooltip>
          );

          const popup = (
            <Popup minWidth={168}>
              <div className="px-3.5 py-3 min-w-[150px]">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-[13px] font-bold text-gray-900 tracking-tight">{p.section} {p.numero}</span>
                  {p.aVenteDvf && (
                    <span className="text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">
                      Vente DVF
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mb-2 -mt-1">{p.nomCom}</p>
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-5 h-5 rounded-md bg-emerald-50 flex items-center justify-center text-[11px] shrink-0">📐</span>
                  <span className="font-medium">{p.contenanceM2.toLocaleString('fr-FR')} m²</span>
                </div>
              </div>
            </Popup>
          );

          const handlers = {
            click: (e: any) => { e.target.bringToFront?.(); onSelect(p); },
            mouseover: (e: any) => { setHoveredIdu(p.idu); e.target.bringToFront?.(); },
            mouseout: () => setHoveredIdu(cur => (cur === p.idu ? null : cur)),
          };

          if (simplified && !isHighlight && !isSelected) {
            return (
              <CircleMarker
                key={p.idu}
                center={[p.centroid[1], p.centroid[0]]}
                radius={isHovered ? 7 : 4}
                pathOptions={{ color: '#fff', weight: isHovered ? 2 : 1, fillColor: color, fillOpacity: 0.9 }}
                eventHandlers={handlers}
              >
                {tip}
                {popup}
              </CircleMarker>
            );
          }

          const positions = toLeafletPositions(p.geometry);

          // Épaisseur / opacité selon l'état — la sélection domine le survol,
          // qui domine l'état au repos.
          const weight = isSelected ? 4 : isHovered ? 3.5 : isHighlight ? 3 : 1.8;
          const fillOpacity = isSelected ? 0.55 : isHovered ? 0.45 : isHighlight ? 0.3 : 0.18;

          return (
            <React.Fragment key={p.idu}>
              {/* Halo blanc dessous : rend la parcelle sélectionnée immédiatement
                  repérable sur une orthophoto, quelle que soit sa couleur. */}
              {isSelected && (
                <Polygon
                  positions={positions}
                  interactive={false}
                  pathOptions={{ color: '#ffffff', weight: 5, opacity: 0.95, fill: false }}
                />
              )}
              <Polygon
                positions={positions}
                pathOptions={{
                  color: isSelected ? '#ffffff' : color,
                  weight,
                  opacity: 1,
                  // La parcelle recherchée passe en pointillés quand une autre est
                  // sélectionnée : on la repère toujours, sans confusion possible
                  // avec la sélection en cours.
                  dashArray: isHighlight && !isSelected && selectedIdu ? '6 5' : undefined,
                  fillColor: color,
                  fillOpacity,
                }}
                eventHandlers={handlers}
              >
                {tip}
                {popup}
              </Polygon>
            </React.Fragment>
          );
        })}
        <Marker position={[lat, lon]} icon={gpsIcon} />
      </MapContainer>
      <div className="flex items-center justify-between gap-4 flex-wrap text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1">📍 Adresse recherchée</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" /> Parcelle recherchée</span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-violet-400 ring-2 ring-white shadow-[0_0_0_1px_rgba(0,0,0,.15)] inline-block" /> Parcelle sélectionnée
          </span>
          <span className="flex items-center gap-1"><span className="w-4 border-t-2 border-dashed border-violet-500 inline-block" /> Zone de recherche</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" /> Sans vente DVF connue</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Vente DVF connue</span>
        </div>
        {simplified && <span>Contours simplifiés en points (zone dense, {parcelles.length} parcelles)</span>}
      </div>
    </div>
  );
}

// Couleur de marque utilisée pour distinguer les grandes parties du document
// imprimé — bandeaux de couleur pleine plutôt que des cadres gris uniformes
// (évite l'effet "feuille d'exercice").
const PRINT_BRAND = 'var(--brand, #4f46e5)';

// Impression via une iframe dédiée : la fiche est clonée seule dans un document
// vierge (avec les feuilles de style de l'app). Imprimer la page de l'app
// elle-même donnait une page blanche : la fiche est enfermée dans des
// conteneurs à défilement/hauteur fixe que le moteur d'impression ne pagine pas.
async function imprimerFiche(node: HTMLElement) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument!;
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map(n => n.outerHTML).join('');
  const clone = node.cloneNode(true) as HTMLElement;
  clone.classList.add('doc');
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><base href="${location.origin}/">${styles}
    <style>
      @page { size: A4; margin: 12mm 13mm; }
      html, body { margin: 0; padding: 0; background: #fff; }
      .no-print { display: none !important; }
      .print-only { display: block !important; }
    </style></head><body>${clone.outerHTML}</body></html>`);
  doc.close();
  const imgs = Array.from(doc.images);
  await Promise.all(imgs.map(img => (img.complete ? null : new Promise(r => { img.onload = img.onerror = r; }))));
  await new Promise(r => setTimeout(r, 150));
  const win = iframe.contentWindow!;
  const nettoyer = () => setTimeout(() => iframe.remove(), 500);
  win.addEventListener('afterprint', nettoyer);
  setTimeout(() => iframe.isConnected && iframe.remove(), 120000);
  win.focus();
  win.print();
}

// Bloc visuel standard pour le document imprimé : bandeau de couleur avec
// icône + titre, contenu sur fond blanc — "break-inside: avoid" (via la
// classe print-block) pour qu'un bloc ne soit jamais coupé en deux entre
// deux pages imprimées.
// Note technique : ce bloc utilise UNIQUEMENT des couleurs de bordure/texte
// (jamais de background-color) pour l'en-tête coloré. Beaucoup de navigateurs
// n'impriment/n'exportent en PDF les couleurs de fond que si l'option
// "graphiques d'arrière-plan" est cochée dans la boîte de dialogue — sinon
// elles disparaissent silencieusement. Les couleurs de bordure et de texte,
// elles, s'impriment toujours : c'est ce qui garantit que les blocs restent
// colorés sur le document final, quel que soit ce réglage.
function PrintBlock({ id, icon, title, right, children, color = PRINT_BRAND }: { id?: string; icon: string; title: string; right?: React.ReactNode; children: React.ReactNode; color?: string }) {
  return (
    <div id={id} data-section={id ? '' : undefined} className="print-block rounded-xl border-2 mb-4 overflow-hidden scroll-mt-14" style={{ borderColor: color }}>
      <div className="pb-head flex items-center justify-between gap-3 px-4 py-2.5 border-b-2" style={{ borderColor: color }}>
        <p className="pb-title text-xs font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color }}>
          <span className="pb-ico">{icon}</span> {title}
        </p>
        {right}
      </div>
      <div className="pb-body p-4">
        {children}
      </div>
    </div>
  );
}

// Fiche cadastrale complète — utilisée à la fois pour la recherche par
// adresse ET pour le détail d'une parcelle cliquée dans "Recherche par
// secteur" : même composant, mêmes infos, aucune version appauvrie.
// Deux options d'impression/export : couleur (par défaut) ou noir & blanc
// (filtre CSS grayscale appliqué à tout le document, avant même d'imprimer,
// pour prévisualiser le rendu) — plus un bouton de partage (Web Share API
// sur mobile/navigateurs compatibles, sinon copie du résumé dans le presse-papiers).
// Raccourcis collés en haut de la fiche : la fiche est longue (cadastre, DVF,
// DPE, risques...), on saute directement à la rubrique voulue. La rubrique
// visible est mise en évidence pendant le défilement.
function RaccourcisFiche({ items, conteneur }: { items: Array<{ id: string; label: string }>; conteneur: React.RefObject<HTMLDivElement> }) {
  const [actif, setActif] = useState(items[0]?.id);
  const barreRef = useRef<HTMLDivElement>(null);
  const cles = items.map(i => i.id).join(',');

  useEffect(() => {
    const racine = conteneur.current;
    if (!racine || typeof IntersectionObserver === 'undefined') return;
    const els = items.map(i => racine.querySelector<HTMLElement>(`#${i.id}`)).filter(Boolean) as HTMLElement[];
    const visibles = new Map<string, boolean>();
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => visibles.set(e.target.id, e.isIntersecting));
      const premier = items.find(i => visibles.get(i.id));
      if (premier) setActif(premier.id);
    }, { rootMargin: '-15% 0px -65% 0px' });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cles]);

  // Garde la puce active visible dans la barre (défilement horizontal)
  useEffect(() => {
    barreRef.current?.querySelector<HTMLElement>(`[data-cible="${actif}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [actif]);

  return (
    <div className="no-print sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-100">
      <div ref={barreRef} className="flex gap-1.5 overflow-x-auto px-4 py-2" style={{ scrollbarWidth: 'none' }}>
        {items.map(i => (
          <button
            key={i.id}
            data-cible={i.id}
            onClick={() => {
              setActif(i.id);
              conteneur.current?.querySelector<HTMLElement>(`#${i.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors ${
              actif === i.id
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-violet-50 hover:text-violet-700'
            }`}
          >
            {i.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Chargement d'une fiche : la recherche interroge une dizaine de sources
// publiques (10 à 20 s parfois). Un simple « Recherche... » donnait
// l'impression que l'appli était plantée : on montre ce qui est en cours.
const ETAPES_CHARGEMENT = [
  "📍 Localisation de l'adresse",
  '🗂️ Recherche de la parcelle cadastrale',
  '🏗️ Analyse des bâtiments',
  "🏙️ Zonage d'urbanisme (PLU)",
  '💰 Historique des ventes (DVF)',
  '⚡ Diagnostic énergétique (DPE)',
  '⚠️ Risques naturels et technologiques',
  '🌐 Éligibilité internet',
  '🚉 Transports et écoles à proximité',
  '📝 Finalisation de la fiche',
];

function ChargementFiche({ compact = false }: { compact?: boolean }) {
  const [etape, setEtape] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setEtape(e => Math.min(e + 1, ETAPES_CHARGEMENT.length - 1)), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" role="status" aria-live="polite">
      <div className="flex items-center gap-3">
        <span className="w-5 h-5 rounded-full border-2 border-violet-200 border-t-violet-600 animate-spin shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Chargement des résultats…</p>
          <p key={etape} className="text-xs text-violet-600 evo-fade">{ETAPES_CHARGEMENT[etape]}</p>
        </div>
      </div>
      <div className="mt-4 h-1 rounded-full bg-violet-100 overflow-hidden">
        <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 evo-indetermine" />
      </div>
      {!compact && (
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1">
          {ETAPES_CHARGEMENT.map((l, i) => (
            <li key={l} className={`text-xs flex items-center gap-1.5 transition-colors ${i < etape ? 'text-gray-400' : i === etape ? 'text-gray-900 font-medium' : 'text-gray-300'}`}>
              <span className="w-3 text-center">{i < etape ? '✓' : i === etape ? '•' : ''}</span>
              {l.replace(/^\S+\s/, '')}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-gray-400 mt-4">Une dizaine de sources publiques sont interrogées — cela peut prendre jusqu'à 20 secondes.</p>
    </div>
  );
}

function FicheCard({ fiche, agent }: { fiche: FicheCadastrale; agent?: AgentProfile | null }) {
  const [printMode, setPrintMode] = useState<'color' | 'bw'>('color');
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const ficheRef = useRef<HTMLDivElement>(null);
  // Couleur de l'agence, déterminée côté serveur selon le réseau du conseiller
  const couleur = agent?.couleurMarque || '#4f46e5';

  // ── Parcelles ajoutées à la main (propriétaire de plusieurs parcelles) ──
  type ParcelleSupp = { parcelle: FicheCadastrale['parcelle']; batiments: FicheCadastrale['batiments']; ventes: FicheCadastrale['ventes'] };
  const [extras, setExtras] = useState<ParcelleSupp[]>([]);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [refSaisie, setRefSaisie] = useState('');
  const [ajoutErreur, setAjoutErreur] = useState<string | null>(null);
  const [ajoutEnCours, setAjoutEnCours] = useState(false);
  useEffect(() => { setExtras([]); setAjoutOuvert(false); }, [fiche.parcelle.idu]);

  const parcelles = [fiche.parcelle, ...extras.map(e => e.parcelle)];
  const surfaceTotale = parcelles.reduce((t, p) => t + (p.contenanceM2 || 0), 0);
  const geometrieGlobale = useMemo(() => (
    extras.length === 0
      ? fiche.parcelle.geometry
      : {
          type: 'MultiPolygon',
          coordinates: [fiche.parcelle, ...extras.map(e => e.parcelle)].flatMap(p =>
            p.geometry.type === 'MultiPolygon' ? p.geometry.coordinates : [p.geometry.coordinates]),
        } as FicheCadastrale['parcelle']['geometry']
  ), [fiche.parcelle, extras]);
  const batiments = useMemo(() => {
    const vus = new Set<string>();
    return [...fiche.batiments, ...extras.flatMap(e => e.batiments)].filter(b => {
      const k = b.id || JSON.stringify(b);
      if (vus.has(k)) return false;
      vus.add(k);
      return true;
    });
  }, [fiche.batiments, extras]);
  const ventes = useMemo(() => {
    const parCle = new Map<string, FicheCadastrale['ventes'][number]>();
    [...fiche.ventes, ...extras.flatMap(e => e.ventes)].forEach(v => {
      const k = `${v.date}|${v.valeur}`;
      if (!parCle.has(k)) parCle.set(k, v);
    });
    return Array.from(parCle.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [fiche.ventes, extras]);
  // Parcelles vendues avec le bien (DVF) mais pas encore sur la fiche : suggestion en un clic
  const parcellesSuggerees = Array.from(new Set(ventes.flatMap(v => v.parcelles || [])))
    .filter(idu => !parcelles.some(p => p.idu === idu));

  // « AS 539 », « as539 », « 0A 12 » ou la référence complète (14 caractères)
  function normaliserReference(saisie: string): string | null {
    const t = saisie.toUpperCase().replace(/[\s\-_.]/g, '');
    if (/^[0-9AB]{5}[0-9]{3}[0-9A-Z]{2}[0-9]{4}$/.test(t)) return t;
    const m = t.match(/^([A-Z]{1,2}|[0-9][A-Z])(\d{1,4})$/);
    if (!m) return null;
    return fiche.parcelle.idu.slice(0, 8) + m[1].padStart(2, '0') + m[2].padStart(4, '0');
  }

  async function ajouterParcelle(saisie: string) {
    const idu = normaliserReference(saisie);
    if (!idu) { setAjoutErreur('Format attendu : section + numéro (ex. AS 539) ou référence complète'); return; }
    if (parcelles.some(p => p.idu === idu)) { setAjoutErreur('Cette parcelle est déjà sur la fiche'); return; }
    setAjoutEnCours(true);
    setAjoutErreur(null);
    try {
      const res = await fetch(`${API}/parcelle-supp?idu=${encodeURIComponent(idu)}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error?.message || data.message || 'Parcelle introuvable');
      setExtras(x => [...x, { parcelle: data.parcelle, batiments: data.batiments || [], ventes: data.ventes || [] }]);
      setRefSaisie('');
      setAjoutOuvert(false);
    } catch (e) {
      setAjoutErreur(e instanceof Error ? e.message : 'Parcelle introuvable');
    } finally {
      setAjoutEnCours(false);
    }
  }

  const summaryText = () =>
    `Fiche cadastrale — ${fiche.adresse.label}, ${fiche.adresse.codePostal} ${fiche.adresse.ville}\n` +
    `Parcelle ${fiche.parcelle.section} ${fiche.parcelle.numero} (${fiche.parcelle.idu}) — ${fiche.parcelle.contenanceM2.toLocaleString('fr-FR')} m²`;

  // Génère un vrai fichier PDF (pas juste un aperçu d'impression) à partir du
  // contenu affiché de la fiche, pour pouvoir le joindre directement à un
  // email/WhatsApp via le partage natif — sans passer par la boîte de dialogue
  // d'impression du navigateur. La classe "pdf-capture" (voir <style> plus bas)
  // masque temporairement les boutons/en-tête d'écran et affiche l'en-tête
  // "papier à en-tête" habituellement réservé à l'impression, le temps de la capture.
  async function generatePdfBlob(): Promise<Blob | null> {
    const node = ficheRef.current;
    if (!node) return null;
    node.classList.add('pdf-capture', 'doc');
    // Largeur A4 fixe (794 px CSS = 210 mm à 96 dpi) : sans ça, la taille du
    // texte dans le PDF dépendait de la largeur du panneau à l'écran (étroit =
    // texte énorme une fois ramené à la largeur A4).
    const largeurInitiale = node.style.width;
    node.style.width = '794px';
    try {
      // Zones à ne jamais couper entre deux pages du PDF (chaque bloc coloré +
      // l'en-tête papier) — mesurées AVANT la capture, en pixels CSS relatifs au
      // conteneur, puis converties à l'échelle du canvas capturé plus bas. Sans
      // ça, la découpe en pages se fait à hauteur fixe et peut couper un bloc
      // (ex: DPE, Risques) en plein milieu.
      const blockEls = Array.from(node.querySelectorAll<HTMLElement>('.print-block, .print-only'));
      const nodeTop = node.getBoundingClientRect().top;
      const unbreakable = blockEls
        .map(el => {
          const r = el.getBoundingClientRect();
          return { start: r.top - nodeTop, end: r.bottom - nodeTop };
        })
        .sort((a, b) => a.start - b.start);

      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const pxPerCssPx = canvas.width / node.offsetWidth;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const MARGE_MM = 10;
      const pageWidthMm = pdf.internal.pageSize.getWidth() - 2 * MARGE_MM;
      const pageHeightMm = pdf.internal.pageSize.getHeight() - 2 * MARGE_MM;
      const pageHeightPx = pageHeightMm * (canvas.width / pageWidthMm);

      let y = 0;
      let firstPage = true;
      while (y < canvas.height) {
        let sliceEnd = Math.min(y + pageHeightPx, canvas.height);
        // Si une zone protégée chevauche la limite de page, on recule la coupure
        // juste avant elle — sauf si ça ne ferait aucun progrès (bloc plus grand
        // qu'une page entière), auquel cas on le coupe quand même en dernier recours.
        for (const zone of unbreakable) {
          const zoneStartPx = zone.start * pxPerCssPx;
          const zoneEndPx = zone.end * pxPerCssPx;
          if (zoneStartPx < sliceEnd && zoneEndPx > sliceEnd && zoneStartPx > y) {
            sliceEnd = zoneStartPx;
          }
        }
        const sliceHeight = Math.max(1, Math.round(sliceEnd - y));

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        }

        const sliceImgData = pageCanvas.toDataURL('image/jpeg', 0.92);
        const sliceHeightMm = (sliceHeight / canvas.width) * pageWidthMm;
        if (!firstPage) pdf.addPage();
        pdf.addImage(sliceImgData, 'JPEG', MARGE_MM, MARGE_MM, pageWidthMm, sliceHeightMm);
        firstPage = false;

        y = sliceEnd;
      }

      return pdf.output('blob');
    } finally {
      node.classList.remove('pdf-capture', 'doc');
      node.style.width = largeurInitiale;
    }
  }

  async function handleShare() {
    const summary = summaryText();
    setSharing(true);
    try {
      const blob = await generatePdfBlob();
      if (blob) {
        const fileName = `fiche-cadastrale-${fiche.parcelle.section}${fiche.parcelle.numero}.pdf`.replace(/\s+/g, '');
        const file = new File([blob], fileName, { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: 'Fiche cadastrale', text: summary });
          } catch {
            // annulé par l'utilisateur — pas d'erreur à afficher
          }
          setSharing(false);
          return;
        }
        // Pas de partage de fichier natif dispo (ex: certains navigateurs desktop) :
        // on télécharge le PDF pour que l'utilisateur le joigne lui-même à un
        // email ou à WhatsApp Web.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setShareFeedback('✅ PDF téléchargé');
        setTimeout(() => setShareFeedback(null), 2500);
        setSharing(false);
        return;
      }
    } catch {
      // Génération du PDF échouée (ex: image satellite bloquée par CORS) —
      // on retombe sur un partage simple (résumé texte + lien), toujours utile.
    }

    const shareData = { title: 'Fiche cadastrale', text: summary, url: window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // annulé par l'utilisateur — pas d'erreur à afficher
      }
      setSharing(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(`${summary}\n${window.location.href}`);
      setShareFeedback('✅ Copié (PDF indispo)');
    } catch {
      setShareFeedback('❌ Échec');
    }
    setTimeout(() => setShareFeedback(null), 2500);
    setSharing(false);
  }

  const generatedAt = new Date();

  return (
    <div
      ref={ficheRef}
      className="fiche-print bg-white rounded-2xl shadow-sm border border-gray-100"
      style={{ ['--brand' as string]: couleur, ...(printMode === 'bw' ? { filter: 'grayscale(1)' } : {}) } as React.CSSProperties}
    >
      <div className="no-print rounded-t-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-white">🏡 {fiche.adresse.label}</h3>
          <p className="text-sm text-violet-100">{fiche.adresse.codePostal} {fiche.adresse.ville}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg overflow-hidden border border-white/25">
            <button
              onClick={() => setPrintMode('color')}
              className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${printMode === 'color' ? 'bg-white text-violet-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
            >
              🎨 Couleur
            </button>
            <button
              onClick={() => setPrintMode('bw')}
              className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${printMode === 'bw' ? 'bg-white text-violet-700' : 'bg-white/10 text-white hover:bg-white/20'}`}
            >
              ◐ Noir &amp; blanc
            </button>
          </div>
          <button
            onClick={handleShare}
            disabled={sharing}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/15 text-white hover:bg-white/25 transition-colors backdrop-blur-sm min-w-[86px] disabled:opacity-60 disabled:cursor-wait"
          >
            {sharing ? '⏳ PDF...' : (shareFeedback || '🔗 Partager')}
          </button>
          <button
            onClick={() => ficheRef.current && imprimerFiche(ficheRef.current)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/15 text-white hover:bg-white/25 transition-colors backdrop-blur-sm"
          >
            🖨️ Imprimer / PDF
          </button>
        </div>
      </div>

      {/* En-tête façon "papier à en-tête", visible uniquement à l'impression —
          fond blanc/texte noir garanti lisible même si l'utilisateur imprime
          sans les couleurs/arrière-plans (case "graphiques d'arrière-plan"
          du navigateur), contrairement au bandeau dégradé ci-dessus. */}
      <div className="print-only doc-header">
        <div className="doc-bar" />
        <div className="doc-header-row">
          {agent ? (
            <div className="doc-agent">
              {agent.photoUrl && <img src={agent.photoUrl} alt={agent.name} className="doc-photo" />}
              <div>
                <p className="doc-agent-name">{agent.name}</p>
                {(agent.activite || agent.entreprise) && (
                  <p className="doc-agent-line">{[agent.activite, agent.entreprise].filter(Boolean).join(' · ')}</p>
                )}
                {(agent.telephone || agent.ville) && (
                  <p className="doc-agent-line">{[agent.telephone, agent.ville].filter(Boolean).join(' · ')}</p>
                )}
              </div>
            </div>
          ) : <div />}
          <div className="doc-meta">
            <p>{parcelles.length > 1 ? `Parcelles ${parcelles.map(p => `${p.section} ${p.numero}`).join(', ')}` : `Réf. cadastrale ${fiche.parcelle.idu}`}</p>
            <p>Édité le {generatedAt.toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
        <p className="doc-kicker">Fiche cadastrale</p>
        <h1 className="doc-title">{fiche.adresse.label}</h1>
      </div>

      <RaccourcisFiche
        conteneur={ficheRef}
        items={[
          { id: 'sec-parcelle', label: '🗂️ Parcelle' },
          ...(fiche.zonage ? [{ id: 'sec-plu', label: '🏙️ PLU' }] : []),
          { id: 'sec-plan', label: '🗺️ Plan' },
          ...(batiments.length > 0 ? [{ id: 'sec-bati', label: '🏗️ Bâti' }] : []),
          { id: 'sec-dvf', label: `💰 Ventes${ventes.length ? ` (${ventes.length})` : ''}` },
          { id: 'sec-dpe', label: '⚡ DPE' },
          { id: 'sec-risques', label: '⚠️ Risques' },
          { id: 'sec-internet', label: '🌐 Internet' },
          { id: 'sec-autoroute', label: '🚗 Autoroute' },
          { id: 'sec-gare', label: '🚉 Gare' },
          { id: 'sec-ecoles', label: '🏫 Écoles' },
        ]}
      />

      <div className="p-6">
      <PrintBlock id="sec-parcelle" icon="🗂️" title="Identité de la parcelle">
        <div className="divide-y divide-gray-100">
          {parcelles.length === 1 ? (
            <>
              <InfoRow icon="🗂️" iconBg="bg-indigo-100" label="Référence (IDU)" value={fiche.parcelle.idu} />
              <InfoRow icon="🏷️" iconBg="bg-violet-100" label="Section / Numéro" value={`${fiche.parcelle.section} ${fiche.parcelle.numero}`} />
            </>
          ) : parcelles.map((p, i) => (
            <div key={p.idu} className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <InfoRow
                  icon="🏷️"
                  iconBg="bg-violet-100"
                  label={`Parcelle ${p.section} ${p.numero}${i === 0 ? ' (adresse)' : ''}`}
                  value={`${p.contenanceM2.toLocaleString('fr-FR')} m²`}
                />
              </div>
              {i > 0 && (
                <button
                  onClick={() => setExtras(x => x.filter(e => e.parcelle.idu !== p.idu))}
                  className="no-print text-xs text-gray-400 hover:text-red-600 px-1"
                  title="Retirer cette parcelle"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <InfoRow icon="📍" iconBg="bg-blue-100" label="Commune" value={`${fiche.parcelle.nomCom} (${fiche.parcelle.codeInsee})`} />
          <InfoRow
            icon="📐"
            iconBg="bg-emerald-100"
            label={parcelles.length > 1 ? `Surface totale (${parcelles.length} parcelles)` : 'Surface'}
            value={`${surfaceTotale.toLocaleString('fr-FR')} m² · ${formatSurfaceCadastrale(surfaceTotale)}`}
          />
          {batiments.some(b => b.empriseSolM2 != null) && (
            <InfoRow
              icon="🏠"
              iconBg="bg-amber-100"
              label="Emprise au sol du bâti"
              value={`${batiments.reduce((s, b) => s + (b.empriseSolM2 || 0), 0).toLocaleString('fr-FR')} m²`}
            />
          )}
          <InfoRow icon="🧭" iconBg="bg-gray-200" label="Coordonnées GPS" value={`${fiche.adresse.lat.toFixed(6)}, ${fiche.adresse.lon.toFixed(6)}`} />
        </div>
        <div className="no-print mt-3 pt-3 border-t border-gray-100 space-y-2">
          {parcellesSuggerees.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-gray-500">Vendues avec ce bien :</span>
              {parcellesSuggerees.map(idu => (
                <button
                  key={idu}
                  onClick={() => ajouterParcelle(idu)}
                  disabled={ajoutEnCours}
                  className="px-2 py-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold hover:bg-indigo-100 disabled:opacity-60"
                >
                  + {idu.slice(8, 10).replace(/^0/, '')} {idu.slice(10).replace(/^0+/, '')}
                </button>
              ))}
            </div>
          )}
          {ajoutOuvert ? (
            <form
              onSubmit={e => { e.preventDefault(); ajouterParcelle(refSaisie); }}
              className="flex items-center gap-2 flex-wrap"
            >
              <input
                autoFocus
                value={refSaisie}
                onChange={e => setRefSaisie(e.target.value)}
                placeholder="Section + numéro (ex. AS 539)"
                className="flex-1 min-w-[180px] px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button type="submit" disabled={ajoutEnCours || !refSaisie.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
                {ajoutEnCours ? '⏳' : 'Ajouter'}
              </button>
              <button type="button" onClick={() => { setAjoutOuvert(false); setAjoutErreur(null); }} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
                Annuler
              </button>
            </form>
          ) : (
            <button onClick={() => setAjoutOuvert(true)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">
              ➕ Ajouter une parcelle (même propriétaire)
            </button>
          )}
          {ajoutErreur && <p className="text-xs text-red-600">{ajoutErreur}</p>}
          <p className="text-[11px] text-gray-400">Section et numéro figurent sur l'avis de taxe foncière ou le titre de propriété. Parcelle de la même commune.</p>
        </div>
      </PrintBlock>

      {fiche.zonage && (
        <PrintBlock
          id="sec-plu"
          icon="🏙️"
          title="Zone d'urbanisme (PLU)"
          right={
            <span className="text-xs font-bold px-2.5 py-1 rounded-full border-2 shrink-0" style={{ borderColor: PRINT_BRAND, color: PRINT_BRAND }}>
              {TYPEZONE_LABELS[fiche.zonage.typezone] || fiche.zonage.typezone}
            </span>
          }
        >
          <p className="font-semibold text-gray-900">
            {fiche.zonage.libelle} — {fiche.zonage.libelong}
          </p>
          {fiche.zonage.ambigu && fiche.zonage.zoneAlternative && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <span className="shrink-0">⚠️</span>
              <span>
                Cette parcelle est proche d'une limite de zonage : selon le point exact considéré, le PLU indique aussi{' '}
                <strong>{fiche.zonage.zoneAlternative.libelle} — {fiche.zonage.zoneAlternative.libelong}</strong>.
                À vérifier auprès de la mairie ou via un certificat d'urbanisme avant toute décision.
              </span>
            </div>
          )}
        </PrintBlock>
      )}

      <PrintBlock id="sec-plan" icon="🗺️" title="Plan cadastral">
        <div className="flex justify-center">
          <ParcelleSatellite
            geometry={geometrieGlobale}
            contenanceM2={surfaceTotale}
            lon={fiche.adresse.lon}
            lat={fiche.adresse.lat}
          />
        </div>
      </PrintBlock>

      {batiments.length > 0 && (
        <PrintBlock id="sec-bati" icon="🏗️" title={`${batiments.length} Bâtiment${batiments.length > 1 ? 's' : ''}`}>
          <AncienneteCarte anciennete={batiments.find(b => b.ancienneteBati)?.ancienneteBati || null} lon={fiche.adresse.lon} lat={fiche.adresse.lat} />
          <div className="space-y-2">
            {batiments.map((b, i) => (
              <div key={b.id || i} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                {batiments.length > 1 && (
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Bâtiment {i + 1}</p>
                )}
                <div className="divide-y divide-slate-200/70">
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <span className="text-slate-500">🏠 Usage</span>
                    <span className="font-semibold text-gray-900 text-right">{b.usage || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <span className="text-slate-500">📐 Emprise au sol</span>
                    <span className="font-semibold text-gray-900 text-right">{b.empriseSolM2 != null ? `${b.empriseSolM2} m²` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <span className="text-slate-500">📏 Hauteur</span>
                    <span className="font-semibold text-gray-900 text-right">{b.hauteurM != null ? `${b.hauteurM.toFixed(1)} m` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <span className="text-slate-500">🪜 Étages</span>
                    <span className="font-semibold text-gray-900 text-right">{b.nombreEtages ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                    <span className="text-slate-500">🔑 Logements</span>
                    <span className="font-semibold text-gray-900 text-right">{b.nombreLogements ?? '—'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Emprise au sol calculée depuis le contour officiel du bâtiment (BD TOPO IGN). Année de
            construction croisée depuis la BDNB (Fichiers fonciers DGFiP + BD TOPO, CSTB) — peut être
            absente (—) si le bâtiment est trop récent pour être référencé ou si le croisement géographique
            échoue. Étages/logements souvent non renseignés selon les communes (qualité de la donnée).
          </p>
        </PrintBlock>
      )}

      <PrintBlock id="sec-dvf" icon="💰" title="Historique des ventes (DVF)">
        {ventes.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune vente enregistrée à cette adresse ni sur cette parcelle dans les données DVF (depuis 2014).</p>
        ) : (
          <div className="space-y-2">
            {ventes.map((v, i) => (
              <div key={i} className="rounded-xl px-3 py-3 bg-orange-50/50 border border-orange-100">
                <div className="flex items-center justify-between text-sm">
                  <p className="font-semibold text-gray-900">📅 Vente le {formatDateFr(v.date)}</p>
                  <div className="text-right">
                    <p className="font-bold text-orange-700">{formatPrice(v.valeur)}</p>
                    {v.prixM2 && <p className="text-xs text-orange-500">{v.prixM2.toLocaleString('fr-FR')} €/m²</p>}
                  </div>
                </div>
                <div className="mt-1.5 pt-1.5 border-t border-orange-100 space-y-0.5">
                  {(() => {
                    // Résumé façon « 1 maison, 1 terrain, 130 m² » (types regroupés)
                    const compte = new Map<string, number>();
                    v.composantes.forEach(c => {
                      const t = (c.type || 'Bien').split(' ')[0].toLowerCase();
                      compte.set(t, (compte.get(t) || 0) + 1);
                    });
                    if (v.nbTerrains) compte.set('terrain', v.nbTerrains);
                    const resume = Array.from(compte.entries())
                      .map(([t, n]) => `${n} ${t}${n > 1 && !t.endsWith('s') ? 's' : ''}`)
                      .join(', ');
                    return (
                      <>
                        <p className="text-xs text-gray-600">
                          {resume}
                          {v.surfaceHabitation ? `, ${v.surfaceHabitation} m²` : ''}
                        </p>
                        {v.surfaceTerrain ? (
                          <p className="text-xs text-gray-500">{v.surfaceTerrain.toLocaleString('fr-FR')} m² de terrain</p>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </PrintBlock>

      <PrintBlock
        id="sec-dpe"
        icon="⚡"
        title="Diagnostic de performance énergétique (DPE ADEME)"
        right={fiche.dpe ? (
          <a
            href={fiche.dpe.lienAdeme}
            target="_blank"
            rel="noopener noreferrer"
            className="no-print shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border border-teal-200 bg-white text-teal-700 hover:bg-teal-100 transition-colors"
          >
            Voir la fiche ADEME ↗
          </a>
        ) : undefined}
      >
        {fiche.dpe ? (
          <div className="rounded-xl px-4 py-4 bg-teal-50/50 border border-teal-100">
            {/* En-tête : badges + type/surface */}
            <div className="flex flex-wrap items-center gap-4 pb-3 border-b border-teal-100">
              <div className="flex gap-3">
                <DpeBadge letter={fiche.dpe.etiquetteDpe} label="DPE" />
                <DpeBadge letter={fiche.dpe.etiquetteGes} label="GES" />
              </div>
              <p className="font-semibold text-gray-900 text-[15px]">
                {fiche.dpe.typeBatiment
                  ? fiche.dpe.typeBatiment.charAt(0).toUpperCase() + fiche.dpe.typeBatiment.slice(1)
                  : 'Logement'}
                {fiche.dpe.surfaceHabitable ? ` · ${fiche.dpe.surfaceHabitable} m²` : ''}
              </p>
            </div>

            {/* Identité / localisation — liste propre, une ligne = une info */}
            <div className="divide-y divide-teal-100/70">
              <DpeInfoRow icon="📅" label="Date d'établissement" value={fiche.dpe.dateEtablissement ? formatDateFr(fiche.dpe.dateEtablissement) : null} />
              <DpeInfoRow
                icon="🏗️"
                label="Période de construction"
                value={fiche.dpe.periodeConstruction}
                note="Période estimée par le DPE — pas l'année exacte"
              />
              <DpeInfoRow icon="🏢" label="Nom de résidence" value={fiche.dpe.nomResidence} />
              <DpeInfoRow icon="📍" label="Adresse brute" value={fiche.dpe.adresseBrute} />
              <DpeInfoRow
                icon="📮"
                label="Adresse (BAN)"
                value={fiche.dpe.adresseBan !== fiche.dpe.adresseBrute ? fiche.dpe.adresseBan : null}
              />
              <DpeInfoRow
                icon="🏷️"
                label="Complément d'adresse"
                value={[fiche.dpe.complementAdresseBatiment, fiche.dpe.complementAdresseLogement].filter(Boolean).join(' · ') || null}
              />
            </div>

            {/* Énergie */}
            <div className="grid grid-cols-2 gap-3 text-sm pt-3 mt-1 border-t border-teal-100">
              <DpeInfoTile icon="🔥" label="Consommation énergie primaire" value={fiche.dpe.consoEnergiePrimaire != null ? `${fiche.dpe.consoEnergiePrimaire} kWhEP/m²/an` : null} />
              <DpeInfoTile icon="🌱" label="Émission GES" value={fiche.dpe.emissionGes != null ? `${fiche.dpe.emissionGes} kgCO₂/m²/an` : null} />
              <DpeInfoTile icon="🔥" label="Énergie chauffage" value={fiche.dpe.typeEnergiePrincipaleChauffage} />
              <DpeInfoTile icon="🚿" label="Énergie eau chaude" value={fiche.dpe.typeEnergiePrincipaleEcs} />
            </div>

            {/* Coûts estimés */}
            {(fiche.dpe.coutChauffage != null || fiche.dpe.coutTotal5Usages != null) && (
              <div className="grid grid-cols-2 gap-3 text-sm pt-3 mt-3 border-t border-teal-100">
                <DpeInfoTile icon="💶" label="Coût chauffage estimé" value={fiche.dpe.coutChauffage != null ? `${formatPrice(fiche.dpe.coutChauffage)}/an` : null} />
                <DpeInfoTile icon="💰" label="Coût total 5 usages estimé" value={fiche.dpe.coutTotal5Usages != null ? `${formatPrice(fiche.dpe.coutTotal5Usages)}/an` : null} />
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Aucun DPE connu à proximité de cette adresse dans les données ADEME.</p>
        )}
      </PrintBlock>

      <PrintBlock id="sec-risques" icon="⚠️" title="Risques naturels et technologiques">
        {fiche.risquesNaturels ? (
          <div className="space-y-3">
            {/* Niveaux réglementaires — sismicité / argiles / radon */}
            <div className="flex flex-wrap gap-2">
              {fiche.risquesNaturels.sismicite && (
                <NiveauPill label="Sismicité" niveau={fiche.risquesNaturels.sismicite.zone} libelle={fiche.risquesNaturels.sismicite.libelle} echelleMax={5} />
              )}
              {fiche.risquesNaturels.argiles && (
                <NiveauPill label="Argiles (retrait-gonflement)" niveau={fiche.risquesNaturels.argiles.code} libelle={fiche.risquesNaturels.argiles.libelle} />
              )}
              {fiche.risquesNaturels.radon && (
                <NiveauPill label="Radon" niveau={fiche.risquesNaturels.radon.classe} libelle={`Potentiel classe ${fiche.risquesNaturels.radon.classe}`} />
              )}
            </div>

            {/* Cavités souterraines */}
            <p className="text-sm text-gray-600">
              🕳️ {fiche.risquesNaturels.nbCavites > 0
                ? `${fiche.risquesNaturels.nbCavites} cavité(s) souterraine(s) connue(s) à moins de 1 km`
                : 'Aucune cavité souterraine connue à moins de 1 km'}
            </p>

            {/* Sites et sols pollués */}
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">🏭 Sites et sols pollués (BASIAS) à proximité :</p>
              {fiche.risquesNaturels.sitesPollues.length > 0 ? (
                <ul className="space-y-0.5 pl-1">
                  {fiche.risquesNaturels.sitesPollues.map((s, i) => (
                    <li key={i} className="text-xs">
                      {s.nom}{s.distanceM != null ? ` — à ${s.distanceM} m` : ''}{s.statut ? ` (statut : ${s.statut})` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400">Aucun site recensé à moins d'1 km.</p>
              )}
            </div>

            {/* Installations classées (ICPE) */}
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">🏗️ Installations classées (ICPE) à proximité :</p>
              {fiche.risquesNaturels.installationsClassees.length > 0 ? (
                <ul className="space-y-0.5 pl-1">
                  {fiche.risquesNaturels.installationsClassees.map((ic, i) => (
                    <li key={i} className="text-xs">
                      {ic.nom}{ic.distanceM != null ? ` — à ${ic.distanceM} m` : ''}
                      {ic.seveso && <span className="ml-1 text-red-600 font-semibold">SEVESO</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400">Aucune installation classée à moins d'1 km.</p>
              )}
            </div>

            {/* Catastrophes naturelles déclarées */}
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">🌪️ Catastrophes naturelles déclarées sur la commune :</p>
              {fiche.risquesNaturels.catnat.length > 0 ? (
                <ul className="space-y-0.5 pl-1">
                  {fiche.risquesNaturels.catnat.slice(0, 5).map((c, i) => (
                    <li key={i} className="text-xs">
                      {c.libelle}{c.dateDebut ? ` — ${c.dateDebut}` : ''}
                    </li>
                  ))}
                  {fiche.risquesNaturels.catnat.length > 5 && (
                    <li className="text-xs text-gray-400">+ {fiche.risquesNaturels.catnat.length - 5} autre(s) arrêté(s)</li>
                  )}
                </ul>
              ) : (
                <p className="text-xs text-gray-400">Aucun arrêté connu sur cette commune.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Données de risques indisponibles pour le moment.</p>
        )}

        <a
          href={`https://www.georisques.gouv.fr/mes-risques/connaitre-les-risques-pres-de-chez-moi?adresse=${encodeURIComponent(fiche.adresse.label)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="no-print inline-block mt-3 text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
        >
          Voir la zone inondable et le détail complet (Géorisques) ↗
        </a>
        <p className="print-only text-sm text-gray-600 mt-2">
          Zone inondable et détail complet à consulter sur georisques.gouv.fr, rubrique "Mes risques", pour l'adresse : {fiche.adresse.label}, {fiche.adresse.codePostal} {fiche.adresse.ville}.
        </p>
      </PrintBlock>

      <PrintBlock id="sec-internet" icon="🌐" title="Éligibilité internet (fibre / ADSL)">
        {fiche.eligibiliteInternet?.status === 'ready' && fiche.eligibiliteInternet.technologies && fiche.eligibiliteInternet.technologies.length > 0 ? (
          <div className="space-y-3">
            {(() => {
              const technos = fiche.eligibiliteInternet.technologies.map((t) => t.technologie);
              const adslOk = technos.includes('CU');
              const fibreOk = technos.includes('FO');
              const pill = (label: string, ok: boolean) => (
                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg border ${
                    ok
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                >
                  {label} : {ok ? 'OUI ✅' : 'NON ❌'}
                </span>
              );
              return (
                <div className="flex flex-wrap gap-2">
                  {pill('ADSL', adslOk)}
                  {pill('Fibre', fibreOk)}
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-1.5">
              {fiche.eligibiliteInternet.technologies.map((t, i) => {
                const label = (t.technologie && TECHNO_LABELS[t.technologie]) || t.technologie || 'Technologie';
                const debit = formatDebit(t.debitDescendant);
                return (
                  <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                    {label}{debit ? ` · ↓ ${debit}` : ''}
                  </span>
                );
              })}
            </div>
            <p className="text-xs text-gray-400">
              Donnée officielle Arcep (Ma connexion internet), immeuble le plus proche
              {fiche.eligibiliteInternet.distanceM != null ? ` (${fiche.eligibiliteInternet.distanceM} m)` : ''}.
            </p>
          </div>
        ) : fiche.eligibiliteInternet?.status === 'importing' ? (
          <p className="text-sm text-gray-500">
            Données Arcep en cours de chargement pour ce département (premier accès, quelques minutes) —
            réessayez cette fiche un peu plus tard pour voir le détail fibre/ADSL.
          </p>
        ) : fiche.eligibiliteInternet?.status === 'ready' ? (
          <p className="text-sm text-gray-400">Aucun immeuble référencé Arcep à proximité immédiate de cette adresse.</p>
        ) : (
          <p className="text-sm text-gray-400">Donnée Arcep temporairement indisponible.</p>
        )}
        <a
          href="https://maconnexioninternet.arcep.fr/"
          target="_blank"
          rel="noopener noreferrer"
          className="no-print inline-block text-xs font-semibold px-3 py-1.5 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors mt-2"
        >
          Vérifier sur Ma Connexion Internet (Arcep) ↗
        </a>
        <p className="print-only text-sm text-gray-600 mt-2">
          À vérifier sur maconnexioninternet.arcep.fr pour l'adresse : {fiche.adresse.label}, {fiche.adresse.codePostal} {fiche.adresse.ville}.
        </p>
      </PrintBlock>

      <PrintBlock id="sec-autoroute" icon="🚗" title="Sortie d'autoroute la plus proche">
        {fiche.sortieAutoroute?.status === 'ready' && fiche.sortieAutoroute.sortie ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">{fiche.sortieAutoroute.sortie.nom}</span>
            {formatTrajet(fiche.sortieAutoroute.sortie) && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                {formatTrajet(fiche.sortieAutoroute.sortie)}
              </span>
            )}
          </div>
        ) : fiche.sortieAutoroute?.status === 'ready' ? (
          <p className="text-sm text-gray-400">Aucune sortie d'autoroute référencée à proximité raisonnable.</p>
        ) : fiche.sortieAutoroute?.status === 'importing' ? (
          <p className="text-sm text-gray-500">Recherche en cours…</p>
        ) : (
          <p className="text-sm text-gray-400">Donnée temporairement indisponible.</p>
        )}
      </PrintBlock>

      <PrintBlock id="sec-gare" icon="🚉" title="Gare la plus proche">
        {fiche.gareSncf?.status === 'ready' && fiche.gareSncf.gare ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">
              {fiche.gareSncf.gare.nom}{fiche.gareSncf.gare.commune ? ` (${fiche.gareSncf.gare.commune})` : ''}
            </span>
            {formatTrajet(fiche.gareSncf.gare) && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                {formatTrajet(fiche.gareSncf.gare)}
              </span>
            )}
          </div>
        ) : fiche.gareSncf?.status === 'ready' ? (
          <p className="text-sm text-gray-400">Aucune gare référencée à proximité raisonnable.</p>
        ) : fiche.gareSncf?.status === 'importing' ? (
          <p className="text-sm text-gray-500">
            Import du référentiel national des gares en cours (premier accès, quelques minutes) — réessayez cette fiche un peu plus tard.
          </p>
        ) : (
          <p className="text-sm text-gray-400">Donnée temporairement indisponible.</p>
        )}
      </PrintBlock>

      <PrintBlock id="sec-ecoles" icon="🏫" title="Écoles à proximité">
        {fiche.ecoles?.status === 'ready' ? (
          <div className="divide-y divide-gray-100">
            {(['maternelle', 'primaire', 'college', 'lycee'] as const).map((niveau) => (
              <EcoleInfoRow key={niveau} niveau={niveau} e={fiche.ecoles?.[niveau]} />
            ))}
          </div>
        ) : fiche.ecoles?.status === 'importing' ? (
          <p className="text-sm text-gray-500">
            Import du référentiel national de l'éducation en cours (premier accès, quelques minutes) — réessayez cette fiche un peu plus tard.
          </p>
        ) : (
          <p className="text-sm text-gray-400">Donnée temporairement indisponible.</p>
        )}
      </PrintBlock>

      <p className="text-xs text-gray-400 mt-2 border-t border-gray-100 pt-3">
        Données cadastrales publiques (IGN — API Carto, Plan Cadastral Informatisé). Ce document ne comporte
        aucune information sur le(s) propriétaire(s) : cette donnée (nom, indivision, succession...) relève
        de la matrice cadastrale, réservée aux professionnels habilités (notaires...) et n'est pas
        accessible via une source publique.
      </p>
      <p className="print-only text-[10px] text-gray-400 mt-2">
        Document généré automatiquement le {generatedAt.toLocaleDateString('fr-FR')} à {generatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} — document non contractuel.
      </p>
      </div>
    </div>
  );
}

export default function CadastrePage() {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fiche, setFiche] = useState<FicheCadastrale | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipNextSuggest = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Profil de l'agent connecté (nom, activité, photo...) — récupéré une fois,
  // utilisé pour personnaliser l'en-tête du document imprimé/partagé.
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  useEffect(() => {
    fetch(`${API}/agent-profile`, { headers: headers() })
      .then(res => res.json())
      .then(data => { if (data.success) setAgentProfile(data.agent); })
      .catch(() => {
        // silencieux — l'en-tête personnalisé est un bonus, pas bloquant
      });
  }, []);

  useEffect(() => {
    if (skipNextSuggest.current) {
      skipNextSuggest.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (address.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    setSuggestions([]);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/suggest?q=${encodeURIComponent(address.trim())}`, { headers: headers(), signal: controller.signal });
        const data = await res.json();
        if (data.success && !controller.signal.aborted) {
          setSuggestions(data.suggestions);
          setShowSuggestions(true);
        }
      } catch {
        // silencieux — l'autocomplétion n'est qu'un confort, pas bloquant
      }
    }, 300);
    return () => {
      controller.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [address]);

  const runLookup = async (params: URLSearchParams) => {
    setLoading(true);
    setError(null);
    setFiche(null);
    setShowSuggestions(false);
    try {
      const res = await fetch(`${API}/lookup?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setFiche(data);
      } else {
        setError(data.error || 'Recherche échouée');
      }
    } catch {
      setError('Erreur réseau');
    }
    setLoading(false);
  };

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    runLookup(new URLSearchParams({ address: address.trim() }));
  };

  const selectSuggestion = (s: Suggestion) => {
    skipNextSuggest.current = true;
    setAddress(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
    runLookup(new URLSearchParams({
      lon: String(s.lon),
      lat: String(s.lat),
      label: s.label,
      city: s.city,
      postcode: s.postcode,
      citycode: s.citycode,
      source: s.source || 'ban',
      positionEstimated: String(Boolean(s.positionEstimated)),
    }));
  };

  // ── Carte unique : toutes les parcelles autour de l'adresse recherchée ──
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [zoneShape, setZoneShape] = useState<ZoneShape>('circle');
  const [zoneRadius, setZoneRadius] = useState(300);
  const [zoneInnerRadius, setZoneInnerRadius] = useState(100);
  const [zoneWidth, setZoneWidth] = useState(600);
  const [zoneHeight, setZoneHeight] = useState(600);
  const [zoneTerrainMin, setZoneTerrainMin] = useState('');
  const [zoneTerrainMax, setZoneTerrainMax] = useState('');
  const [zoneLoading, setZoneLoading] = useState(false);
  const [zoneError, setZoneError] = useState<string | null>(null);
  const [zoneResult, setZoneResult] = useState<ZoneResult | null>(null);
  // Paramètres réellement utilisés par la dernière recherche exécutée (figés au
  // clic sur "Actualiser", contrairement aux sliders qui bougent en continu) —
  // sert à dessiner le contour de la zone et cadrer la carte dessus.
  const [zoneResultParams, setZoneResultParams] = useState<ZoneParams | null>(null);

  const searchZone = async () => {
    if (!fiche) return;
    setZoneLoading(true);
    setZoneError(null);
    setZoneResult(null);
    const params = new URLSearchParams({
      lon: String(fiche.adresse.lon),
      lat: String(fiche.adresse.lat),
      shape: zoneShape,
    });
    if (zoneShape === 'rectangle') {
      params.set('width', String(zoneWidth));
      params.set('height', String(zoneHeight));
    } else {
      params.set('radius', String(zoneRadius));
      if (zoneShape === 'donut') params.set('innerRadius', String(zoneInnerRadius));
    }
    if (zoneTerrainMin) params.set('terrainMin', zoneTerrainMin);
    if (zoneTerrainMax) params.set('terrainMax', zoneTerrainMax);
    try {
      const res = await fetch(`${API}/zone?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setZoneResult(data);
        setZoneResultParams({
          shape: zoneShape,
          radiusM: zoneRadius,
          innerRadiusM: zoneInnerRadius,
          widthM: zoneWidth,
          heightM: zoneHeight,
        });
      } else {
        setZoneError(data.error || 'Recherche de zone échouée');
      }
    } catch {
      setZoneError('Erreur réseau');
    }
    setZoneLoading(false);
  };

  // Panneau de détail (façon SaaS pro) affiché quand on clique une parcelle sur la carte/le tableau.
  // Par défaut (aucune sélection), le panneau affiche la fiche de l'adresse recherchée.
  const [selectedParcelle, setSelectedParcelle] = useState<ParcelleZone | null>(null);
  const [selectedFiche, setSelectedFiche] = useState<FicheCadastrale | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const selectParcelle = async (p: ParcelleZone) => {
    // Reclic sur la parcelle déjà affichée par défaut (l'adresse recherchée) → on revient simplement dessus
    if (fiche && p.idu === fiche.parcelle.idu) {
      setSelectedParcelle(null);
      setSelectedFiche(null);
      return;
    }
    setSelectedParcelle(p);
    setSelectedFiche(null);
    setSelectedLoading(true);
    try {
      const params = new URLSearchParams({
        lon: String(p.centroid[0]),
        lat: String(p.centroid[1]),
        label: `${p.section} ${p.numero}`,
        city: p.nomCom,
        citycode: p.codeInsee,
      });
      const res = await fetch(`${API}/lookup?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (data.success) setSelectedFiche(data);
    } catch {
      // silencieux — le panneau affichera juste les infos déjà connues (référence, surface)
    }
    setSelectedLoading(false);
  };

  // Dès qu'une nouvelle adresse est recherchée, on charge automatiquement les
  // parcelles alentour (zone par défaut) — plus besoin de bouton "Explorer le secteur".
  useEffect(() => {
    if (fiche) {
      setSelectedParcelle(null);
      setSelectedFiche(null);
      searchZone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiche?.parcelle.idu]);

  const panelFiche = selectedFiche || fiche;

  return (
    <div className="max-w-[1800px] mx-auto space-y-5">
      <style>{`
        .print-only { display: none; }
        @keyframes evo-indetermine { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        .evo-indetermine { animation: evo-indetermine 1.3s ease-in-out infinite; }
        @keyframes evo-fade { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        .evo-fade { animation: evo-fade .35s ease-out; }
        /* Utilisée pendant la capture html2canvas (génération du PDF à partager) :
           reproduit hors média "print" les mêmes réglages d'affichage que @media print,
           pour que le PDF partagé ressemble à la version imprimée (en-tête papier,
           sans les boutons d'écran), sans dépendre de window.print(). */
        .pdf-capture.fiche-print {
          box-shadow: none !important; border: none !important; border-radius: 0 !important;
        }
        /* html2canvas ne tient jamais compte de "@media print" (il capture le DOM
           tel qu'affiché à l'écran) — donc la réduction de taille appliquée à
           l'impression native plus bas ne s'applique pas ici toute seule. Comme
           les classes Tailwind utilisent des rem (relatifs à <html>, pas au plus
           proche parent), on réduit directement la police racine dès qu'un
           descendant porte la classe "pdf-capture" (:has), le temps de la capture. */
        html:has(.pdf-capture) { font-size: 13px; }
        .pdf-capture .no-print { display: none !important; }
        .pdf-capture .print-only { display: block !important; }
        .pdf-capture, .pdf-capture * {
          -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
        }
        /* overflow-hidden sur .print-block sert à faire suivre les coins arrondis à
           l'écran, mais Chrome ignore "break-inside: avoid" sur tout élément dont
           l'overflow n'est pas "visible" — c'est ce qui coupait les blocs en deux
           entre deux pages à l'impression. On repasse donc en coins carrés
           (overflow visible) uniquement en contexte impression/capture PDF. */
        .pdf-capture .print-block {
          overflow: visible !important;
          border-radius: 0 !important;
          margin-top: 6mm;
        }
        /* ===== Mode document (impression et PDF partagé) =====
           Tailles fixes en px pensées pour une page A4, filets fins à la
           couleur de l'agence au lieu de gros cadres épais. */
        .doc { font-size: 11px; color: #1f2937; box-shadow: none !important; border: none !important; border-radius: 0 !important; overflow: visible !important; }
        .doc > .p-6 { padding: 0 !important; }
        .doc .text-xs, .doc .text-\[10px\] { font-size: 9px !important; line-height: 1.4 !important; }
        .doc .text-sm { font-size: 10.5px !important; line-height: 1.45 !important; }
        .doc .text-base { font-size: 11.5px !important; }
        .doc .text-lg, .doc .text-xl { font-size: 13px !important; }
        .doc .truncate { overflow: visible !important; white-space: normal !important; }
        .doc .rounded-xl, .doc .rounded-2xl { border-radius: 4px !important; }
        .doc .info-ico, .doc .pb-ico { display: none !important; }
        .doc .py-2\.5 { padding-top: 3px !important; padding-bottom: 3px !important; }
        .doc .py-3 { padding-top: 6px !important; padding-bottom: 6px !important; }
        .doc .py-1\.5 { padding-top: 2px !important; padding-bottom: 2px !important; }
        .doc .space-y-2 > * + * { margin-top: 5px !important; }
        .doc .print-block { border: none !important; border-radius: 0 !important; overflow: visible !important; margin: 0 0 14px !important; break-inside: avoid; page-break-inside: avoid; }
        .doc .pb-head { padding: 0 0 3px !important; border-bottom-width: 1.5px !important; }
        .doc .pb-title { font-size: 10px !important; letter-spacing: .08em; }
        .doc .pb-body { padding: 7px 0 0 !important; }
        .doc .max-w-sm { max-width: 260px !important; }
        .doc-header { padding: 0 0 12px; margin-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
        .doc-bar { height: 5px; background: var(--brand); margin-bottom: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .doc-header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
        .doc-agent { display: flex; align-items: center; gap: 10px; }
        .doc-photo { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; border: 2px solid var(--brand); }
        .doc-agent-name { font-size: 12px; font-weight: 700; color: #111827; line-height: 1.3; }
        .doc-agent-line { font-size: 9.5px; color: #6b7280; line-height: 1.4; }
        .doc-meta { text-align: right; font-size: 9px; color: #6b7280; line-height: 1.5; }
        .doc-kicker { margin-top: 14px; font-size: 9px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--brand); }
        .doc-title { font-size: 17px; font-weight: 700; color: #111827; line-height: 1.25; margin-top: 2px; }
        .doc, .doc * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        @media print {
          .print-only { display: block !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 no-print">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">📋 Fiche cadastrale</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Entre une adresse pour récupérer le plan de la parcelle, sa référence cadastrale (section, numéro)
          et sa surface — à partir des données publiques IGN, imprimable directement.
        </p>
        <form onSubmit={search} className="flex gap-2 mt-4 relative">
          <div className="flex-1 relative">
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Ex: 12 rue de la République, 38140 Rives"
              autoComplete="off"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                {/^\s*\d/.test(address) && suggestions.every(s => s.type !== 'housenumber') && (
                  <div className="px-3 py-2 text-xs text-amber-600 bg-amber-50 border-b border-amber-100">
                    Le numéro saisi n’a pas été trouvé dans la base d’adresses. Précisez la commune ou le code postal.
                    Sélectionner une rue utilisera une position approximative : choisissez ensuite la bonne parcelle sur la carte.
                  </div>
                )}
                {suggestions.map((s, i) => (
                  <button
                    type="button"
                    key={i}
                    onMouseDown={() => selectSuggestion(s)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-gray-50 last:border-b-0"
                  >
                    <span className="font-medium text-gray-900">{s.label}</span>
                    {s.positionEstimated && (
                      <span className="ml-2 text-xs text-amber-600">(position estimée du numéro)</span>
                    )}
                    {s.type !== 'housenumber' && (
                      <span className="ml-2 text-xs text-amber-600">(position approximative de la rue ou du lieu-dit)</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 transition-all shadow-sm disabled:opacity-60"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Recherche…
              </span>
            ) : 'Rechercher'}
          </button>
        </form>
        {error && <p className="text-sm text-red-500 mt-3">❌ {error}</p>}
        {fiche?.adresse.positionEstimated && (
          <p className="text-sm text-amber-700 mt-3">
            Adresse trouvée. La position du numéro est estimée le long de la voie : vérifiez la parcelle sur la carte.
          </p>
        )}
      </div>

      {loading && !fiche && <ChargementFiche />}

      {fiche && (
        <div className="grid lg:grid-cols-[1fr_440px] gap-5 items-start">
          {/* ── Colonne principale : une seule carte, tout cliquable ── */}
          <div className="no-print min-w-0 space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1.5">
                  {(['circle', 'donut', 'rectangle'] as ZoneShape[]).map(s => (
                    <button
                      key={s}
                      onClick={() => setZoneShape(s)}
                      className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                        zoneShape === s
                          ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-transparent shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {s === 'circle' ? '⭕ Cercle' : s === 'donut' ? '🍩 Donut' : '▭ Rectangle'}
                    </button>
                  ))}
                </div>

                {zoneShape !== 'rectangle' ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{zoneShape === 'donut' ? 'Rayon externe' : 'Rayon'}</span>
                    <input
                      type="range" min={100} max={2000} step={50}
                      value={zoneRadius}
                      onChange={e => setZoneRadius(Number(e.target.value))}
                      className="w-28"
                    />
                    <span className="font-semibold text-gray-700 w-14">{zoneRadius} m</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">L
                      <input type="range" min={100} max={2000} step={50} value={zoneWidth} onChange={e => setZoneWidth(Number(e.target.value))} className="w-20" />
                      <span className="font-semibold text-gray-700 w-12">{zoneWidth}m</span>
                    </span>
                    <span className="flex items-center gap-1.5">H
                      <input type="range" min={100} max={2000} step={50} value={zoneHeight} onChange={e => setZoneHeight(Number(e.target.value))} className="w-20" />
                      <span className="font-semibold text-gray-700 w-12">{zoneHeight}m</span>
                    </span>
                  </div>
                )}

                {zoneShape === 'donut' && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Rayon interne</span>
                    <input
                      type="range" min={0} max={Math.max(zoneRadius - 50, 0)} step={50}
                      value={Math.min(zoneInnerRadius, Math.max(zoneRadius - 50, 0))}
                      onChange={e => setZoneInnerRadius(Number(e.target.value))}
                      className="w-20"
                    />
                    <span className="font-semibold text-gray-700 w-14">{zoneInnerRadius} m</span>
                  </div>
                )}

                <button
                  onClick={() => setShowAdvancedFilters(o => !o)}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  ⚙️ Filtres {showAdvancedFilters ? '▲' : '▼'}
                </button>

                <button
                  onClick={searchZone}
                  disabled={zoneLoading}
                  className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 transition-all shadow-sm disabled:opacity-60"
                >
                  {zoneLoading ? 'Recherche...' : '🔄 Actualiser'}
                </button>
              </div>

              {showAdvancedFilters && (
                <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className="font-semibold text-gray-500">📐 Surface du terrain</span>
                  <input
                    type="number"
                    value={zoneTerrainMin}
                    onChange={e => setZoneTerrainMin(e.target.value)}
                    placeholder="min m²"
                    className="w-24 border border-gray-200 rounded-lg px-2 py-1"
                  />
                  <input
                    type="number"
                    value={zoneTerrainMax}
                    onChange={e => setZoneTerrainMax(e.target.value)}
                    placeholder="max m²"
                    className="w-24 border border-gray-200 rounded-lg px-2 py-1"
                  />
                  <span className="text-gray-400">
                    Pas encore disponibles : polygone à main levée, filtre emprise au sol du bâti, filtre piscine
                    (pas de source publique fiable identifiée pour ces deux derniers).
                  </span>
                </div>
              )}
            </div>

            {zoneError && <p className="text-sm text-red-500">❌ {zoneError}</p>}

            <CadastreMap
              lat={fiche.adresse.lat}
              lon={fiche.adresse.lon}
              parcelles={zoneResult?.parcelles || []}
              onSelect={selectParcelle}
              highlightIdu={fiche.parcelle.idu}
              selectedIdu={selectedParcelle?.idu || fiche.parcelle.idu}
              zone={zoneResultParams}
            />
            {zoneLoading && (
              <p className="text-xs text-gray-400 text-center -mt-2">Chargement des parcelles alentour...</p>
            )}

            {zoneResult && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-center">
                    <p className="text-lg font-bold text-violet-700">🗂️ {zoneResult.total}</p>
                    <p className="text-xs text-violet-500">Parcelle{zoneResult.total > 1 ? 's' : ''}</p>
                  </div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-center">
                    <p className="text-lg font-bold text-emerald-700">📐 {formatSurfaceCadastrale(zoneResult.surfaceTotaleM2)}</p>
                    <p className="text-xs text-emerald-500">Surface cumulée</p>
                  </div>
                  <div className="rounded-xl bg-orange-50 border border-orange-100 px-4 py-3 text-center">
                    <p className="text-lg font-bold text-orange-700">💰 {zoneResult.nbAvecVenteDvf}</p>
                    <p className="text-xs text-orange-500">Avec vente DVF connue</p>
                  </div>
                </div>
                {zoneResult.capped && (
                  <p className="text-xs text-amber-600">Résultat limité par l'API — réduis la zone pour tout voir.</p>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                          <th className="pb-2 pt-3 pl-3 font-medium">Référence</th>
                          <th className="pb-2 pt-3 font-medium">Commune</th>
                          <th className="pb-2 pt-3 font-medium">Surface</th>
                          <th className="pb-2 pt-3 pr-3 font-medium">DVF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zoneResult.parcelles.map(p => (
                          <tr
                            key={p.idu}
                            onClick={() => selectParcelle(p)}
                            className={`border-b border-gray-50 cursor-pointer hover:bg-violet-50/60 transition-colors ${
                              (selectedParcelle?.idu || fiche.parcelle.idu) === p.idu ? 'bg-violet-50' : ''
                            }`}
                          >
                            <td className="py-1.5 pl-3 font-medium text-gray-900">{p.section} {p.numero}</td>
                            <td className="py-1.5 text-gray-500">{p.nomCom}</td>
                            <td className="py-1.5 text-gray-900">{p.contenanceM2.toLocaleString('fr-FR')} m²</td>
                            <td className="py-1.5 pr-3">
                              {p.aVenteDvf ? (
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-orange-50 text-orange-600">vente connue</span>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Panneau de détail, collé à la carte (sticky) — jamais besoin de descendre tout en bas ── */}
          <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto space-y-2">
            {selectedParcelle && (
              <div className="no-print flex items-center justify-between px-1">
                <p className="text-xs font-semibold text-gray-500">📌 Parcelle sélectionnée sur la carte</p>
                <button
                  onClick={() => { setSelectedParcelle(null); setSelectedFiche(null); }}
                  className="text-xs font-semibold text-violet-600 hover:text-violet-800"
                >
                  ↩ Revenir à l'adresse recherchée
                </button>
              </div>
            )}
            {selectedLoading ? (
              <ChargementFiche compact />
            ) : (
              panelFiche && <FicheCard fiche={panelFiche} agent={agentProfile} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
